<script setup lang="ts">
  import { ref, onMounted, onUnmounted, toRefs, getCurrentInstance, nextTick, computed } from "vue";
  import { useRoute } from "vue-router";
  import axios from "axios";
  import { reactive } from "vue";
  import dayjs from "dayjs";
  import _ from "lodash";
  
  import { version1NameList, version2NameList, periodNameList } from "@/utils/hardcode";
  import { queryParamsProps, versionItemProps } from "@/views/dp/client/interface/index.ts";
  import LazyLoadMovieSelect from "@/components/LazyLoadMovieSelect/index.vue";
  import ChartCom from "@/components/chart/index.vue";
  import {
    getMovieListService,
    getCompanyNameService,
    getAreaChartService,
    getAreaListService,
    getPrefectureCountService,
    getMovieReviewService,
  } from "@/api/dp/index.js";
  
  let proxy: any = null;
  const instance = getCurrentInstance();
  const route = useRoute();
  const companyId = window.localStorage.getItem('deptId');
  // const companyId = 1244;
  if (instance && instance.proxy) {
    proxy = instance.proxy;
  }
  
  onMounted(() => {
    getCompanyNameService({
      companyId: Number(companyId),
    }).then((res) => {
      companyName.value = res;
    });
  
    getAreaListService().then((res) => {
      areaList.value = res.rows.map((item: { id: number; name: string }) => ({
        id: item.id,
        name: item.name,
      }));
    });
  });
  
  // reactive
  const queryParams = reactive<queryParamsProps>({
    movieId: undefined,
    duration: [],
  });
  const areaQueryParams = reactive<{
    areaId: number | undefined;
  }>({
    areaId: undefined,
  });
  const { movieId, duration } = toRefs(queryParams);
  // ref
  const isFetch = ref(true);
  const companyName = ref();
  const areaList = ref([]);
  const appendVersion1Names = ref<
    {
      name: string;
      width: number;
    }[]
  >([]);
  const appendVersion2Names = ref<
    {
      name: string;
      width: number;
    }[]
  >([]);
  const cinemaTotalTableData = ref<
    {
      countTotal: number;
    }[]
  >([]);
  const tableData = ref<
    {
      countTotal: number;
    }[]
  >([]);
  const areaTableData = ref<
    {
      id?: any;
      areaName: string;
      prefectureName: string;
      cinemaTotalNo: number;
      countTotal: number;
      seatTotalNo: number;
      emptyRate: number | null;
    }[]
  >([]);
  const detailTableData = ref<
    {
      prefectureName: string;
      brandName: string;
      cinemaName: string;
      countTotal: number;
      seatTotalNo: number;
      emptyRate: number;
      isBrandRow: boolean;
      level: "brand" | "cinema";
      isFirstInGroup: boolean;
    }[]
  >([]);
  const isShowDetail = ref(false);
  const SeatTotalTableData = ref<
    {
      seatTotalNo: number;
      maxBoxOffice: number;
      minBoxOffice: number;
      emptyRate: number;
      morningSeatTotalNo: number;
      afternoonSeatTotalNo: number;
      nightSeatTotalNo: number;
      morningEmptyRate: number;
      afternoonEmptyRate: number;
      nightEmptyRate: number;
    }[]
  >([]);
  const AIPredictionTableData = ref<
    {
      seatTotalNo: number;
    }[]
  >([]);
  const version1ListData = ref<versionItemProps[]>([]);
  const version2ListData = ref<versionItemProps[]>([]);
  const periodListData = ref<
    {
      name: string;
      count: number;
    }[]
  >([]);
  const periodSeatTotalData = ref<
    {
      name: string;
      count: number;
    }[]
  >([]);
  const periodSeatOccupiedRateData = ref<
    {
      name: string;
      count: number;
    }[]
  >([]);
  const AIPredictionSeatTotalNo = computed(() => {
    if (SeatTotalTableData.value.length === 0) {
      return 0;
    }
    const seatTotalNo = SeatTotalTableData.value[0].seatTotalNo;
    const maxBoxOffice = SeatTotalTableData.value[0].maxBoxOffice;
    const minBoxOffice = SeatTotalTableData.value[0].minBoxOffice;
    const rate = SeatTotalTableData.value[0].emptyRate / 100;
  
    const formatNumber = (num: number) => {
      if (num >= 100000000) {
        return (num / 100000000).toFixed(2) + "億";
      } else if (num >= 10000000) {
        return (num / 10000000).toFixed(2) + "千万";
      } else if (num >= 10000) {
        return (num / 10000).toFixed(2) + "万";
      } else {
        return num.toFixed(2);
      }
    };
    const filterVersionList = version1ListData.value.filter((item) => 
      item.name === 'IMAX' ||
      item.name === '4DX' ||
      item.name === 'MX4D' ||
      item.name === 'ScreenX' ||
      item.name === '4DX SCREEN' ||
      item.name === 'IMAXレーザー' ||
      item.name === 'IMAXレーザー/GT'
   );
    console.log(minBoxOffice, maxBoxOffice);
    const minValue = minBoxOffice;
    const maxValue = maxBoxOffice;

    return `${formatNumber(minValue)} ~ ${formatNumber(maxValue)}`;
  });
  const commentCount = ref(0);
  const likeCount = ref(0);
  const movieList = ref<
    {
      name: string;
      id: number;
    }[]
  >([]);
  const loading = ref(false);
  const detailLoading = ref(false);
  
  const rules = ref({
    movieId: [
      {
        required: true,
        trigger: "change",
        message: "作品を選択してください",
      },
    ],
  });
  const areaRules = ref({
    areaId: [
      {
        required: true,
        trigger: "change",
        message: "エリアを選択してください",
      },
    ],
  });
  const chartData = ref();
  const areaChartData = ref();
  const isShowMovieSelect = ref(false);
  const hasExpandedPrefecture = ref(false); // 跟踪是否有展开的都道府県节点
  const hasExpandedBrand = ref(false); // 跟踪是否有展开的映画館チェーン节点
  const commentObj = ref<any>({});
  
  //根据返回的version名字mapping 获取count
  const getCountForVersionName = (name: string, versionList: versionItemProps[]) => {
    const count = (versionList.find((item, idx) => item.name === name) || {}).count;
    if (count !== undefined && count !== null) {
      return count.toLocaleString();
    }
    return 0;
  };
  const getCountForPeriodName = (name: string, periodList: { name: string; count: number }[]) => {
    const count = (periodList.find((item, idx) => item.name === name) || {}).count;
    if (count !== undefined && count !== null) {
      return count.toLocaleString();
    }
    return 0;
  };
  const getRateForPeriodNum = (name: string, periodList: { name: string; count: number }[]) => {
    const count = (periodList.find((item, idx) => item.name === name) || {}).count;
    if (count !== undefined && count !== null) {
      return count;
    }
    return 0;
  };
  const getDiffArrays = (versionList: any[], versionNameList: any[]) => {
    const diffArrs = _.cloneDeep(
      _.differenceBy(
        versionList.map((item: { versionId: string; name: string }) => ({
          id: item.versionId,
          name: item.name,
        })),
        versionNameList,
        "name",
      ),
    );
    return diffArrs.map((item) => ({
      name: item.name,
      width: 120,
    }));
  };
  const getList = () => {
    proxy.$refs.queryParamsRef.validate((valid: boolean) => {
      if (valid) {
        loading.value = true;
        axios({
          method: "get",
          url: "/api/dp/scheduleCount",
          params: {
            movieId: movieId.value,
            ...(duration.value
              ? {
                  dateFrom: dayjs(duration.value[0]).startOf("day").valueOf(),
                  dateTo: dayjs(duration.value[1]).add(1, "day").startOf("day").valueOf(),
                }
              : {}),
          },
        })
          .then((res: any) => {
            getMovieReviewService({
              movieId: movieId.value,
            }).then((reviewRes: any) => {
              // 保存当前的评论list
              if (!reviewRes) {
                commentCount.value = 0;
                likeCount.value = 0;
              } else {
                commentCount.value = reviewRes[0].rateCount;
                likeCount.value = reviewRes[0].wantToSee + reviewRes[0].haveSeen;
                commentObj.value = reviewRes[0];
              }
            });
            loading.value = false;
            // 检查是否有新增的Version
  
            const {
              countTotal = 0,
              maxBoxOffice = 0,
              minBoxOffice = 0,
              cinemaTotalNo = 0,
              version1List = [],
              version2List = [],
              seatTotalNo = 0,
              emptyRate = 0,
              morningCountTotal = 0,
              afternoonCountTotal = 0,
              nightCountTotal = 0,
              morningSeatTotalNo,
              afternoonSeatTotalNo,
              nightSeatTotalNo,
              morningEmptyRate,
              afternoonEmptyRate,
              nightEmptyRate,
            } = res.data || {};
            const difVersions1 = getDiffArrays(version1List, version1NameList);
            const difVersions2 = getDiffArrays(version2List, version2NameList);
            if (difVersions1 && difVersions1.length > 0) {
              appendVersion1Names.value = difVersions1;
            }
            if (difVersions2 && difVersions2.length > 0) {
              appendVersion2Names.value = difVersions2;
            }
            version1ListData.value = version1List;
            version2ListData.value = version2List;
            periodListData.value = [
              {
                name: "午前",
                count: morningCountTotal || 0,
              },
              {
                name: "午後",
                count: afternoonCountTotal || 0,
              },
              {
                name: "夜",
                count: nightCountTotal || 0,
              },
            ];
            cinemaTotalTableData.value = [{ countTotal: cinemaTotalNo }];
            tableData.value = [{ countTotal }];
            SeatTotalTableData.value = [
              {
                seatTotalNo,
                maxBoxOffice,
                minBoxOffice,
                emptyRate,
                morningSeatTotalNo: morningSeatTotalNo || 0,
                afternoonSeatTotalNo: afternoonSeatTotalNo || 0,
                nightSeatTotalNo: nightSeatTotalNo || 0,
                morningEmptyRate,
                afternoonEmptyRate,
                nightEmptyRate,
              },
            ];
  
            // set chart
            // const dates = chatDataList.map((item: { date: string }) => item.date);
            // const counts = chatDataList.map((item: { countTotal: number }) => item.countTotal);
            // const seats = chatDataList.map((item: { seatTotalNo: number }) => item.seatTotalNo);
            // const rates = chatDataList.map((item: { emptyRate: number}) => item.emptyRate);
            chartData.value = res.data.chatDataList;
          })
          .catch((error) => {
            chartData.value = [];
            tableData.value = [{ countTotal: 0 }];
            loading.value = false;
          });
      }
    });
    // axios
    //   .get(`/api/dp/scheduleCount?movieId=${movieId}&dateForm=${duration[0]}&dateTo=${duration[1]}`)
    //   .then((res: any) => {
    //     loading.value = false;
    //     const { countTotal = 0, version1List = [], version2List = [] } = res || {};
    //     versionList.value = [...version1List, ...version2List];
    //     tableData.value = [{ countTotal }]
    //   })
    //   .catch((error) => {
    //     loading.value = false;
    //   });
  };
  const getRate = (seatOccupiedNo: number, seatTotalNo: number) => {
    return seatTotalNo === 0
      ? "0%"
      : Math.round((seatOccupiedNo / seatTotalNo) * 100 * 10) / 10 + "%";
  };
  const getMovieListFn = () => {
    loading.value = true;
    chartData.value = [];
    movieId.value = "";
    getMovieListService({
      companyId,
      ...(duration.value && duration.value.length > 0
        ? {
            startTime: dayjs(duration.value[0]).startOf("day").valueOf(),
            endTime: dayjs(duration.value[1]).add(1, "day").startOf("day").valueOf(),
          }
        : {}),
      page: 0,
      pageSize: 50,
    })
      .then((res: any) => {
        loading.value = false;
        movieList.value = res;
        isShowMovieSelect.value = true;
        if (res.length < 50) {
          nextTick(() => {
            isFetch.value = false;
          });
        }
      })
      .catch((error) => {
        loading.value = false;
      });
  };
  const getAreaChart = () => {
    const { areaId } = areaQueryParams;
    proxy.$refs.areaQueryParamsRef.validate((valid: boolean) => {
      if (valid) {
        loading.value = true;
        getAreaChartService({
          areaId,
          movieId: movieId.value,
          ...(duration.value
            ? {
                dateFrom: dayjs(duration.value[0]).startOf("day").valueOf(),
                dateTo: dayjs(duration.value[1]).add(1, "day").startOf("day").valueOf(),
              }
            : {}),
        })
          .then((res) => {
            loading.value = false;
            areaTableData.value = res.areaScheduleList.flatMap(
              (area: {
                areaName: string;
                preFectureScheduleList: {
                  prefectureId: string;
                  prefectureName: string;
                  countTotal: number;
                  cinemaTotalNo: number;
                  seatTotalNo: number;
                  emptyRate: number;
                }[];
              }) =>
                area.preFectureScheduleList.map((prefecture, idx) => ({
                  areaName: idx === 0 ? area.areaName : "",
                  prefectureName: prefecture.prefectureName,
                  prefectureId: prefecture.prefectureId,
                  countTotal: prefecture.countTotal,
                  cinemaTotalNo: prefecture.cinemaTotalNo,
                  seatTotalNo: prefecture.seatTotalNo,
                  emptyRate: prefecture.emptyRate,
                })),
            );
            // append row alltotal
            areaTableData.value.push({
              areaName: "合計",
              prefectureName: "",
              countTotal: res.scheduleTotal,
              seatTotalNo: res.seatTotal,
              emptyRate: res.emptyRate,
              cinemaTotalNo: res.cinemaTotalNo,
            });
            areaChartData.value = res.chatDataList;
          })
          .catch((err) => {
            loading.value = false;
            areaChartData.value = [];
            areaTableData.value = [];
          });
      }
    });
  };
  // 处理详细按钮点击
  const handleDetail = (row: any) => {
    console.log("查看详细:", row);
    isShowDetail.value = true;
    detailLoading.value = true;
    // detailTableData.value = row.children;
    getPrefectureCountService({
      movieId: movieId.value,
      prefectureId: row.prefectureId,
      ...(duration.value
        ? {
            dateFrom: dayjs(duration.value[0]).startOf("day").valueOf(),
            dateTo: dayjs(duration.value[1]).add(1, "day").startOf("day").valueOf(),
          }
        : {}),
    }).then((res) => {
      detailLoading.value = false;
      if (!res.brandCinemaSchedules || res.brandCinemaSchedules.length === 0) {
        detailTableData.value = [];
        return;
      }
      // 扁平化数据：将嵌套的数组结构转换为扁平数组
      const flatData: any[] = [];
  
      res.brandCinemaSchedules.forEach((brand: any) => {
        // 先添加品牌级别的汇总行
        flatData.push({
          prefectureName: res.prefectureName,
          brandName: brand.brandName,
          cinemaName: "", // 品牌行不显示具体电影院
          countTotal: brand.countTotal || 0, // 使用 brand 的 countTotal（品牌汇总）
          seatTotalNo: brand.seatTotalNo || 0, // 使用 brand 的 seatTotalNo（品牌汇总）
          emptyRate: brand.emptyRate || 0, // 使用 brand 的 emptyRate（品牌汇总）
          morningCountTotal: brand.morningCountTotal || 0,
          afternoonCountTotal: brand.afternoonCountTotal || 0,
          nightCountTotal: brand.nightCountTotal || 0,
          morningSeatTotalNo: brand.morningSeatTotalNo || 0,
          afternoonSeatTotalNo: brand.afternoonSeatTotalNo || 0,
          nightSeatTotalNo: brand.nightSeatTotalNo || 0,
          morningEmptyRate: brand.morningEmptyRate || 0,
          afternoonEmptyRate: brand.afternoonEmptyRate || 0,
          nightEmptyRate: brand.nightEmptyRate || 0,
          isBrandRow: true, // 标记这是品牌行
          level: "brand", // 标记层级
          isFirstInGroup: true, // 标记这是组内第一行
        });
  
        // 再添加该品牌下所有电影院的具体行
        brand.prefectureCinemaSchedules.forEach((cinema: any, cinemaIndex: number) => {
          flatData.push({
            prefectureName: res.prefectureName,
            brandName: brand.brandName,
            cinemaName: cinema.cinemaName,
            countTotal: cinema.countTotal || 0, // 使用 cinema 的 countTotal（电影院具体数据）
            seatTotalNo: cinema.seatTotalNo || 0,
            emptyRate: cinema.emptyRate || 0,
            morningCountTotal: cinema.morningCountTotal || 0,
            afternoonCountTotal: cinema.afternoonCountTotal || 0,
            nightCountTotal: cinema.nightCountTotal || 0,
            morningSeatTotalNo: cinema.morningSeatTotalNo || 0,
            afternoonSeatTotalNo: cinema.afternoonSeatTotalNo || 0,
            nightSeatTotalNo: cinema.nightSeatTotalNo || 0,
            morningEmptyRate: cinema.morningEmptyRate || 0,
            afternoonEmptyRate: cinema.afternoonEmptyRate || 0,
            nightEmptyRate: cinema.nightEmptyRate || 0,
            isBrandRow: false, // 标记这是电影院行
            level: "cinema", // 标记层级
            isFirstInGroup: false, // 标记这不是组内第一行
          });
        });
      });
  
      detailTableData.value = flatData;
      console.log("扁平化后的数据:", flatData);
    });
    // 这里可以添加查看详细的逻辑，比如：
    // - 打开详情弹窗
    // - 跳转到详情页面
    // - 显示更多数据等
  };
  const selectPeriod_fn = () => {
    // 重置loadmore
    nextTick(() => {
      isFetch.value = true;
    });
    if (movieId.value) {
      movieId.value = "";
    }
    resetData();
  };
  const resetData = () => {
    tableData.value = [{ countTotal: 0 }];
    SeatTotalTableData.value = [];
    areaTableData.value = [
      {
        id: "empty",
        areaName: "",
        countTotal: 0,
        prefectureName: "",
        seatTotalNo: 0,
        emptyRate: 0,
        cinemaTotalNo: 0,
      },
    ];
  
    chartData.value = [];
    areaChartData.value = [];
    hasExpandedPrefecture.value = false; // 重置展开状态
    hasExpandedBrand.value = false; // 重置映画館チェーン展开状态
  };
  const disabledDate = (time: Date) => {
    const targetDate = new Date("2025-03-16");
    return time < targetDate;
  };
  const getProgressPercentage = (item: any, number: number) => {
    if (!number) {
      return 0;
    }
    const { rate1Count = 0, rate2Count = 0, rate3Count = 0, rate4Count = 0, rate5Count = 0 } = item;
    return (number / (rate1Count + rate2Count + rate3Count + rate4Count + rate5Count)) * 100;
  };
  const getStarClass = (rating: number) => {
    // 判断rating是否为整数
    if (Number.isInteger(rating)) {
      return `star-${rating}`;
    }
    // 否则返回小数点后的第一位
    return `star-${Math.floor(rating)}-5`;
  };
  const getRatingCount = (rating: number) => {
    if (!rating) {
      return 0;
    }
    if (rating <= 1000) {
      return rating;
    }
    return (rating / 1000).toFixed(1) + "K";
  };
  </script>
  
  <template>
    <div v-loading="loading" style="padding: 30px">
      <!--  filter options  -->
      <el-form ref="queryParamsRef" :model="queryParams" :rules="rules" label-width="60">
        <el-row>
          <el-col>
            <el-form-item label="会社">
              <!-- companyName -->
              <span class="name">{{ companyName }}</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="期間">
              <el-date-picker
                v-model="queryParams.duration"
                type="daterange"
                start-placeholder=""
                end-placeholder=""
                :default-value="[new Date(), new Date()]"
                :disabled-date="disabledDate"
                @change="selectPeriod_fn"
              />
            </el-form-item>
          </el-col>
          <el-col :span="2">
            <el-button
              type="primary"
              style="
                width: 100%;
                box-shadow: 0px 1px 5px 0px #0000001f;
                box-shadow: 0px 2px 2px 0px #00000024;
                box-shadow: 0px 3px 1px -2px #00000033;
                border: none;
              "
              @click="getMovieListFn"
              >検索</el-button
            >
          </el-col>
        </el-row>
        <el-row v-if="isShowMovieSelect" :gutter="20">
          <el-col :span="5">
            <el-form-item label="作品" prop="movieId">
              <!-- <el-input v-model="queryParams.movieId" placeholder="名前で検索" /> -->
              <!-- <el-select-v2 v-model="movieId" placeholder="名前で検索" filterable :options="movieList" /> -->
              <LazyLoadMovieSelect
                ref="lazyLoadRef"
                v-model:model-value="movieId"
                :list="movieList"
                :fetch-options="getMovieListService"
                :company-id="companyId"
                :dates="queryParams.duration"
                :is-fetch="isFetch"
                @change="resetData"
                valueKey="id"
                labelKey="name"
              />
            </el-form-item>
          </el-col>
          <el-col :span="2">
            <el-button
              type="primary"
              style="
                width: 100%;
                box-shadow: 0px 1px 5px 0px #0000001f;
                box-shadow: 0px 2px 2px 0px #00000024;
                box-shadow: 0px 3px 1px -2px #00000033;
                border: none;
              "
              @click="getList"
              >確定</el-button
            >
          </el-col>
        </el-row>
      </el-form>
      <el-divider />
      <!-- Statistical charts -->
      <div class="charts-container">
        <p class="title" style="font-size: 16px">期間内合計</p>
        <ChartCom :chart-list="chartData" type="line" />
      </div>
      <!--  table  -->
      <template v-if="tableData && tableData.length > 0">
        <div>
          <el-table :data="cinemaTotalTableData" class="movieTable">
            <el-table-column label="上映館数" prop="countTotal">
              <template #default="{ row }">
                {{ row.countTotal.toLocaleString() }}
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div>
          <el-table :data="tableData" class="movieTable">
            <el-table-column label="上映回数（合計）" prop="countTotal" width="140">
              <template #default="{ row }">
                {{ row.countTotal.toLocaleString() }}
              </template>
            </el-table-column>
            <el-table-column
              v-for="(item, versionIdx) in version1NameList"
              :key="item.name"
              :label="item.name"
              :width="
                versionIdx === version1NameList.length - 1 && appendVersion1Names.length === 0
                  ? 'auto'
                  : item.width
              "
            >
              <template #default>
                {{ getCountForVersionName(item.name, version1ListData) }}
              </template>
            </el-table-column>
            <el-table-column
              v-for="(item, appendIdx) in appendVersion1Names"
              :key="item.name"
              :label="item.name"
              :width="appendIdx === appendVersion1Names.length - 1 ? 'auto' : item.width"
            >
              <template #default>
                {{ getCountForVersionName(item.name, version1ListData) }}
              </template>
            </el-table-column>
          </el-table>
          <el-table :data="tableData" class="movieTable">
            <el-table-column width="140"></el-table-column>
            <el-table-column
              v-for="item in version2NameList"
              :key="item.name"
              :label="item.name"
              :width="item.width"
            >
              <template #default>
                {{ getCountForVersionName(item.name, version2ListData) }}
              </template>
            </el-table-column>
            <el-table-column
              v-for="item in appendVersion2Names"
              :key="item.name"
              :label="item.name"
              :width="item.width"
            >
              <template #default>
                {{ getCountForVersionName(item.name, version1ListData) }}
              </template>
            </el-table-column>
          </el-table>
          <el-table :data="tableData" class="movieTable">
            <el-table-column width="140"></el-table-column>
            <el-table-column
              v-for="item in periodNameList"
              :key="item.name"
              :label="item.name"
              :width="item.width"
            >
              <template #default>
                {{ getCountForPeriodName(item.name, periodListData) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
        <hr style="margin: 50px 0" />
        <div v-loading="loading" style="margin-bottom: 66px">
          <el-table :data="SeatTotalTableData" class="movieTable">
            <el-table-column label="座席数（合計）" prop="seatTotalNo" width="140">
              <template #default="{ row }">
                {{ row.seatTotalNo ? row.seatTotalNo.toLocaleString() : 0 }}
              </template>
            </el-table-column>
            <el-table-column
              v-for="item in periodNameList"
              :key="item.name"
              :label="item.name"
              :width="item.width"
            >
              <template #default="{ row }">
                {{ (row[`${item.value}SeatTotalNo`] || 0).toLocaleString() }}
              </template>
            </el-table-column>
            <el-table-column width="100"></el-table-column>
            <el-table-column width="100"></el-table-column>
            <!-- <el-table-column label="動員数 (ネット予約終了まで)" prop="seatOccupiedNo" width="220">
              <template #default="{ row }">
                {{ row.seatOccupiedNo ? row.seatOccupiedNo.toLocaleString() : 0 }}
              </template>
            </el-table-column> -->
            <el-table-column width="260" label="充足率 (ネット予約終了まで)">
              <template #default="{ row }">
                {{ row.emptyRate + "%" }}
              </template>
            </el-table-column>
            <el-table-column
              v-for="item in periodNameList"
              :key="item.name"
              :label="item.name"
              :width="item.width"
            >
              <template #default="{ row }">
                {{ row[`${item.value}EmptyRate`] + "%" }}
              </template>
            </el-table-column>
          </el-table>
          <el-table
            :data="[
              {
                seatTotalNo: AIPredictionSeatTotalNo,
              },
            ]"
            class="movieTable"
          >
            <el-table-column label="興行収入（AI推測値）（円）" prop="seatTotalNo"></el-table-column>
          </el-table>
          <!-- <el-table
            :data="[
              {
                commentCount: commentCount,
                likeCount: likeCount,
              },
            ]"
            class="movieTable"
          >
            <el-table-column label="コメント数" prop="commentCount" width="120"></el-table-column>
            <el-table-column label="いいね数" prop="likeCount"></el-table-column>
          </el-table> -->
          <!-- comment area -->
          <div class="comment-area">
            <div v-if="commentObj" class="progress-bar-container">
              <div v-for="(item, idx) in 5" :key="idx" class="progress-item">
                <span style="width: 10px; margin-right: 6px">{{ 5 - idx }}</span>
                <ProgressBar
                  :percentage="getProgressPercentage(commentObj, commentObj[`rate${5 - idx}Count`])"
                />
                <span style="margin-left: 6px">{{
                    getProgressPercentage(commentObj, commentObj[`rate${5 - idx}Count`]).toFixed(1)
                  }}%</span>
              </div>
            </div>
            <div class="star-info-container">
              <!-- <p class="star-score">{{ (comment?.rating || 0).toFixed(1) }}</p> -->
              <p class="star-score">
                {{ commentObj?.rating ? commentObj?.rating.toFixed(1) : "未評価" }}
              </p>
              <!-- <div class="star-container" style="display: flex; justify-content: center">
              <img src="@/images/star.svg" alt="star" />
              <img src="@/images/star.svg" alt="star" />
              <img src="@/images/star.svg" alt="star" />
              <img src="@/images/star.svg" alt="star" />
            </div> -->
              <div :class="['stars', getStarClass(commentObj?.rating)]"></div>
              <p class="votes">{{ getRatingCount(commentObj?.rateCount) }} Votes</p>
              <div class="like-info">
                <img src="@/assets/images/like.svg" alt="like" />
                <span>みたい！</span>
              </div>
              <p class="like-count">{{ getRatingCount(commentObj?.wantToSee) }}</p>
              <div class="saw-info">
                <img src="@/assets/images/saw.svg" alt="like" />
                <span>みた！</span>
              </div>
              <p class="saw-count">{{ getRatingCount(commentObj?.haveSeen) }}</p>
            </div>
          </div>
        </div>
        <el-divider />
      </template>
      <!-- Statistical charts -->
      <div v-if="movieId" class="charts-container" style="margin-top: 51px">
        <p class="title" style="font-size: 16px">期間内合計（エリア別）</p>
        <el-form
          ref="areaQueryParamsRef"
          :model="areaQueryParams"
          :rules="areaRules"
          label-width="80"
        >
          <el-row :gutter="20">
            <el-col :span="5">
              <el-form-item label="エリア" prop="areaId">
                <!-- <el-input v-model="queryParams.movieId" placeholder="名前で検索" /> -->
                <el-select-v2
                  v-model="areaQueryParams.areaId"
                  placeholder="名前で検索"
                  filterable
                  clearable
                  :options="areaList"
                  :props="{
                    value: 'id',
                    label: 'name',
                  }"
                />
              </el-form-item>
            </el-col>
            <el-col :span="2">
              <el-button
                type="primary"
                style="
                  width: 100%;
                  box-shadow: 0px 1px 5px 0px #0000001f;
                  box-shadow: 0px 2px 2px 0px #00000024;
                  box-shadow: 0px 3px 1px -2px #00000033;
                  border: none;
                "
                @click="getAreaChart"
                >確定</el-button
              >
            </el-col>
          </el-row>
        </el-form>
        <ChartCom :chart-list="areaChartData" type="line"></ChartCom>
        <!-- table -->
        <el-table :data="areaTableData" class="areaTable">
          <el-table-column label="地方" prop="areaName" width="100"></el-table-column>
          <el-table-column label="都道府県" prop="prefectureName" width="100"></el-table-column>
          <el-table-column label="上映館数" prop="cinemaTotalNo" width="140">
            <template #default="{ row }">
              {{
                row.cinemaTotalNo !== undefined &&
                row.cinemaTotalNo !== null &&
                row.cinemaTotalNo !== ""
                  ? row.cinemaTotalNo.toLocaleString()
                  : ""
              }}
            </template>
          </el-table-column>
          <el-table-column label="上映回数" prop="countTotal" width="140">
            <template #default="{ row }">
              {{
                row.countTotal !== undefined && row.countTotal !== null && row.countTotal !== ""
                  ? row.countTotal.toLocaleString()
                  : ""
              }}
            </template>
          </el-table-column>
          <el-table-column label="座席数" prop="seatTotalNo" width="140">
            <template #default="{ row }">
              {{
                row.seatTotalNo !== undefined && row.seatTotalNo !== null && row.seatTotalNo !== ""
                  ? row.seatTotalNo.toLocaleString()
                  : ""
              }}
            </template>
          </el-table-column>
          <el-table-column label="充足率" prop="emptyRate" width="140">
            <template #default="{ row }">
              {{
                row.emptyRate !== undefined && row.emptyRate !== null && row.emptyRate !== ""
                  ? row.emptyRate + "%"
                  : ""
              }}
            </template>
          </el-table-column>
          <el-table-column width="130">
            <template #default="{ row }">
              <a
                href="javascript:void(0)"
                style="color: var(--brand-color); text-decoration: none"
                @click="handleDetail(row)"
              >
                詳細
              </a>
            </template>
          </el-table-column>
        </el-table>
        <!-- 详细弹窗 -->
        <el-dialog
          v-model="isShowDetail"
          title="都道府県の詳細"
          width="80%"
          :close-on-click-modal="false"
        >
          <el-table v-loading="detailLoading" :data="detailTableData" class="detailTable">
            <el-table-column label="都道府県" prop="prefectureName" width="80">
              <template #default="{ row }">
                <span v-if="row.isFirstInGroup" style="font-weight: bold">{{
                  row.prefectureName
                }}</span>
                <span v-else style="color: transparent">{{ row.prefectureName }}</span>
              </template>
            </el-table-column>
            <el-table-column label="映画館チェーン" prop="brandName" width="130">
              <template #default="{ row }">
                <span v-if="row.isFirstInGroup" style="font-weight: bold">{{ row.brandName }}</span>
                <span v-else style="color: transparent">{{ row.brandName }}</span>
              </template>
            </el-table-column>
            <el-table-column label="映画館" prop="cinemaName" width="140">
              <template #default="{ row }">
                <span v-if="row.isBrandRow" style="font-weight: bold"></span>
                <span v-else>{{ row.cinemaName }}</span>
              </template>
            </el-table-column>
            <el-table-column label="上映回数" prop="countTotal" width="90">
              <template #default="{ row }">
                <span
                  :style="{
                    // color: row.isBrandRow ? '#f9da49' : '#181818',
                    fontWeight: row.isBrandRow ? 'bold' : 'normal',
                  }"
                >
                  {{ row.countTotal.toLocaleString() }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              v-for="item in periodNameList"
              :key="item.name"
              :label="item.name"
              :width="70"
            >
              <template #header>
                <span style="color: #181818">
                  {{ item.name }}
                </span>
              </template>
              <template #default="{ row }">
                <span
                  :style="{
                    color: '#181818',
                    fontWeight: row.isBrandRow ? 'bold' : 'normal',
                  }"
                >
                  {{ (row[`${item.value}CountTotal`] || 0).toLocaleString() }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="座席数" prop="seatTotalNo" width="90">
              <template #default="{ row }">
                <span
                  :style="{
                    // color: row.isBrandRow ? '#f9da49' : '#181818',
                    fontWeight: row.isBrandRow ? 'bold' : 'normal',
                  }"
                >
                  {{ row.seatTotalNo.toLocaleString() }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              v-for="item in periodNameList"
              :key="item.name"
              :label="item.name"
              :width="70"
              row-class-name="duration"
            >
              <template #header>
                <span style="color: #181818">
                  {{ item.name }}
                </span>
              </template>
              <template #default="{ row }">
                <span
                  :style="{
                    // color: row.isBrandRow ? '#f9da49' : '#181818',
                    fontWeight: row.isBrandRow ? 'bold' : 'normal',
                  }"
                >
                  {{ (row[`${item.value}SeatTotalNo`] || 0).toLocaleString() }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="充足率" prop="emptyRate" width="90">
              <template #default="{ row }">
                <span
                  :style="{
                    // color: row.isBrandRow ? '#f9da49' : '#181818',
                    fontWeight: row.isBrandRow ? 'bold' : 'normal',
                  }"
                >
                  {{ row.emptyRate + "%" }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              v-for="item in periodNameList"
              :key="item.name"
              :label="item.name"
              :width="70"
            >
              <template #header>
                <span style="color: #181818">
                  {{ item.name }}
                </span>
              </template>
              <template #default="{ row }">
                <span
                  :style="{
                    // color: row.isBrandRow ? '#f9da49' : '#181818',
                    fontWeight: row.isBrandRow ? 'bold' : 'normal',
                  }"
                >
                  {{ row[`${item.value}EmptyRate`] + "%" }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </el-dialog>
      </div>
    </div>
  </template>
  <style lang="less" scoped>
  .company-name {
    margin-bottom: 24px;
  
    > span {
      font-size: 16px;
      letter-spacing: 0.9px;
      color: #181818;
      margin-right: 22px;
  
      &.name {
        font-size: 12px;
        letter-spacing: -0.83px;
        font-weight: 700;
      }
    }
  }
  
  .charts-container {
    title {
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 0.9px;
    }
  }
  .areaTable,
  .movieTable {
    ::v-deep(thead .el-table__cell) {
      font-weight: 700;
      color: var(--brand-color, #cc251b);
    }
    ::v-deep(tbody .el-table__cell) {
      font-size: 14px;
      font-weight: 500;
      color: #181818;
    }
  }
  
  // 列过渡动画样式
  .column-fade-enter-active,
  .column-fade-leave-active {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .column-fade-enter-from {
    opacity: 0;
    transform: translateX(-50px);
    width: 0;
  }
  
  .column-fade-enter-to {
    opacity: 1;
    transform: translateX(0);
    width: auto;
  }
  
  .column-fade-leave-from {
    opacity: 1;
    transform: translateX(0);
    width: auto;
  }
  
  .column-fade-leave-to {
    opacity: 0;
    transform: translateX(-50px);
    width: 0;
    color: var(--brand-color, #cc251b);
  }
  
  // 表格列动画优化
  .areaTable {
    ::v-deep(.el-table__header-wrapper) {
      .el-table__cell {
        transition: all 0.3s ease-in-out;
      }
    }
  }
  
  // 详细弹窗表格样式
  .detailTable {
    ::v-deep(.el-table__header-wrapper) {
      .el-table__cell {
        color: var(--brand-color, #cc251b) !important; /* 只改变表头文字颜色 */
      }
    }
  
    /* 确保表格内容文字保持黑色 */
    ::v-deep(.el-table__body-wrapper) {
      .el-table__cell {
        color: #181818 !important; /* 表格内容文字保持黑色 */
      }
    }
  }
  .movieTable {
    ::v-deep(thead .el-table__cell) {
      font-weight: 700;
      color: #181818;
    }
  }
  .comment-area {
    display: flex;
    margin-top: 30px;
    .progress-bar-container {
      // margin-left: 26px;
      // width: 110px;
      .progress-item {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 16px;
        span {
          font-size: 14px;
          font-weight: 400;
          letter-spacing: 0.17px;
        }
        .el-progress {
          width: 110px;
        }
      }
    }
    .star-info-container {
      margin-left: 22px;
      margin-top: -10px;
      .stars {
        margin-left: -10px;
        width: 143px;
        height: 24px;
        background: url("https://eigaland.com/assets/eigaland/images/star.png") center top no-repeat;
        &.star-0 {
          background-position: center -26px;
        }
        &.star-1 {
          background-position: center -50px;
        }
        &.star-1-5 {
          background-position: center -74px;
        }
        &.star-2 {
          background-position: center -98px;
        }
        &.star-2-5 {
          background-position: center -121px;
        }
        &.star-3 {
          background-position: center -146px;
        }
        &.star-3-5 {
          background-position: center -168px;
        }
        &.star-4 {
          background-position: center -190px;
        }
        &.star-4-5 {
          background-position: center -212px;
        }
        &.star-5 {
          background-position: center -237px;
        }
      }
      p {
        margin: 0;
        padding: 0;
        &.star-score {
          font-size: 24px;
          font-weight: 500;
          letter-spacing: 0;
          text-align: left;
        }
        &.votes {
          font-size: 16px;
          font-weight: 400;
          color: #999999;
          letter-spacing: 0.4px;
        }
      }
      img {
        width: 14px;
        height: 14px;
        margin-right: 1.8px;
      }
      .like-info,
      .saw-info {
        margin-top: 2px;
        // margin-bottom: 4px;
        display: flex;
        align-items: center;
        color: #666666;
        font-size: 16px;
        font-weight: 800;
        > img {
          margin-right: 3px;
        }
      }
      .like-count,
      .saw-count {
        font-size: 16px;
        font-weight: 800;
        color: #f9b749;
      }
    }
  }
  </style>
  